import React, { useContext } from 'react';
import axios from 'axios';
import Modal from 'components/Modal';
import Icon from 'components/Icon';
import Tooltip from 'components/Tooltip';
import { isUserMlsStaff } from 'utils';
import { useHistory } from 'react-router-dom';
import { UniversalSearchContext, CrossOrgAnsweringContext } from 'contexts';
import { Space } from 'antd';
import { AnsweringSubscriptionTag } from '@bb/domain/dist/types';
import assume from '../assume';
import {
  StyledButton,
  StyledButtonWrapper,
  StyledFlexWrapper,
} from '../StyledComponents';
import { AuthType, OrgDetailsType, RepType } from '../types';

type AnsweringListingActionButtonsProps = {
  auth: AuthType;
  org: OrgDetailsType;
  id: string;
  reps: RepType[];
  forceBlur: () => void;
  index: number;
  subscriptionTag: AnsweringSubscriptionTag;
  preBookingInformation?: string;
};

const AnsweringListingActionButtons = ({
  auth,
  org,
  id,
  reps,
  forceBlur,
  index,
  subscriptionTag,
  preBookingInformation,
}: AnsweringListingActionButtonsProps) => {
  const history = useHistory();
  const { activeRow } = useContext(UniversalSearchContext);
  const { answeringConfirm } = useContext(CrossOrgAnsweringContext);
  const listingOrgHasPrebooking = org.features.includes('PRE_BOOKING');

  if (activeRow !== index) return null;
  const renderModal = () => {
    Modal.confirm({
      icon: null,
      zIndex: 4000,
      title: 'Important Information',
      content: `${preBookingInformation}`,
      okText: 'Continue to booking',
      cancelText: 'Cancel',
      closable: true,
    });
  };
  return (
    <>
      {auth.assumerIdMlsStaff || auth.isAssumedNotBB ? (
        <StyledButton
          icon={<Icon type="calendar" width={16} />}
          data-cy="schedule-dropdown-button"
          onClick={e => {
            e.stopPropagation();
            answeringConfirm(
              {
                type: 'listing',
                subscriptionTag,
                listingOrgHasPrebooking,
                preBookingInformation,
              },
              () => {
                assume(
                  org.id,
                  Boolean(auth.isAssumedNotBB),
                  `/listing/${id}/appointments/book`,
                );
              },
            )();
          }}
        >
          Schedule
        </StyledButton>
      ) : (
        <StyledFlexWrapper
          $justifyContent="flex-end"
          onClick={e => e.stopPropagation()}
        >
          <Space>
            <StyledButton
              icon={<Icon type="calendar" width={16} />}
              data-cy="schedule-dropdown-button"
              onClick={answeringConfirm(
                {
                  type: 'listing',
                  subscriptionTag,
                  listingOrgHasPrebooking,
                  preBookingInformation,
                },
                () => {
                  forceBlur();
                  history.push(`/listing/${id}/appointments/book`);
                },
              )}
            >
              Schedule
            </StyledButton>

            {!isUserMlsStaff(auth) && (
              <Tooltip
                title={
                  reps.length
                    ? 'Message agent(s)'
                    : 'Online chat is not currently available for the listing agent(s).'
                }
                data-cy="message-agent-tooltip"
              >
                <StyledButtonWrapper disabled={!reps.length}>
                  <StyledButton
                    shape="circle"
                    disabled={!reps.length}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      answeringConfirm(
                        { type: 'listing', subscriptionTag },
                        () => {
                          axios.post('/messaging/newConvo', {
                            userIds: reps.map(
                              ({ id: repId }: { id: string }) => repId,
                            ),
                          });
                        },
                      )();
                    }}
                    icon={<Icon type="chatBubble" width={16} />}
                    data-cy="listing-chat-button"
                  />
                </StyledButtonWrapper>
              </Tooltip>
            )}
          </Space>
        </StyledFlexWrapper>
      )}
    </>
  );
};

export default AnsweringListingActionButtons;
